import { z } from 'zod';
import { AddressSchema } from '../../../backend_config.types.js';
import {
    JsonLosslessNumberInputSchema,
    JsonNonnegativeLosslessNumberInputSchema,
    JsonPositiveLosslessNumberInputSchema,
    PhoneNumberSchema,
} from '../../common.schemas.js';

import type {
    DealertrackDocumentType,
    DealertrackArStatementCustomersTransactionDetails,
    DealertrackDealTransactionExtension,
    DealertrackLeasingTransactionExtension,
    DealertrackPartsLineItem,
    DealertrackPartsTotals,
    DealertrackServiceBillingTotals,
    DealertrackServiceTotalsByPillar,
    DealertrackTransactionDetails,
} from '../../../types/ipw/dealertrack/dealertrack-transaction-details.js';
import { DealertrackDocumentTypeValues } from '../../../types/ipw/dealertrack/dealertrack-transaction-details.js';
import { ThirdPartySystemId } from '../../../types/third-party-system.enums.js';
import { TransactionDetailsTypeId } from '../../../types/transaction-details.enums.js';

export const DealertrackDocumentTypeSchema: z.ZodType<DealertrackDocumentType> = z.enum(
    DealertrackDocumentTypeValues
);

export const DealertrackPartsLineItemSchema: z.ZodType<DealertrackPartsLineItem> = z.object({
    optionCode: z.string().optional(),
    partNumber: z.string().optional(),
    description: z.string().optional(),
    binShelf: z.string().optional(),
    quantity: JsonLosslessNumberInputSchema.optional(),
    available: JsonLosslessNumberInputSchema.optional(),
    listPrice: JsonLosslessNumberInputSchema.optional(),
    netPrice: JsonLosslessNumberInputSchema.optional(),
    extendedPrice: JsonLosslessNumberInputSchema.optional(),
});

export const DealertrackPartsTotalsSchema: z.ZodType<DealertrackPartsTotals> = z.object({
    freight: JsonLosslessNumberInputSchema.optional(),
    fees: JsonLosslessNumberInputSchema.optional(),
    partsSubtotal: JsonLosslessNumberInputSchema.optional(),
    taxes: JsonLosslessNumberInputSchema.optional(),
    deposit: JsonLosslessNumberInputSchema.optional(),
    total: JsonLosslessNumberInputSchema.optional(),
});

export const DealertrackPartsTransactionExtensionSchema = z.object({
    partsInvoiceNumber: z.string().optional(),
    closeDate: z.string().optional(),
    paymentMethod: z.string().optional(),
    controlReference: z.string().optional(),
    accountCardLast4: z.string().optional(),
    counterperson: z.string().optional(),
    status: z.string().optional(),
    transactionType: z.string().optional(),
    customerAccountNumber: z.string().optional(),
    mainPhone: PhoneNumberSchema.optional(),
    poNumber: z.string().optional(),
    priceLevel: z.string().optional(),
    saleType: z.string().optional(),
    totals: DealertrackPartsTotalsSchema.optional(),
    lineItems: z.array(DealertrackPartsLineItemSchema).optional(),
});

export const DealertrackServiceTotalsByPillarSchema: z.ZodType<DealertrackServiceTotalsByPillar> =
    z.object({
        labor: JsonLosslessNumberInputSchema.optional(),
        parts: JsonLosslessNumberInputSchema.optional(),
        deductible: JsonLosslessNumberInputSchema.optional(),
        sublet: JsonLosslessNumberInputSchema.optional(),
        shopSupplies: JsonLosslessNumberInputSchema.optional(),
        paintMaterials: JsonLosslessNumberInputSchema.optional(),
        miscCharges: JsonLosslessNumberInputSchema.optional(),
        totalCharges: JsonLosslessNumberInputSchema.optional(),
        salesTax: JsonLosslessNumberInputSchema.optional(),
        totalDue: JsonLosslessNumberInputSchema.optional(),
    });

export const DealertrackServiceBillingTotalsSchema: z.ZodType<DealertrackServiceBillingTotals> =
    z.object({
        customerPay: DealertrackServiceTotalsByPillarSchema.optional(),
        warranty: DealertrackServiceTotalsByPillarSchema.optional(),
        internal: DealertrackServiceTotalsByPillarSchema.optional(),
        serviceContract: DealertrackServiceTotalsByPillarSchema.optional(),
    });

export const DealertrackServiceTransactionExtensionSchema = z.object({
    closeDate: z.string().optional(),
    paymentMethod: z.string().optional(),
    controlReference: z.string().optional(),
    accountCardLast4: z.string().optional(),
    roNumber: z.string().optional(),
    status: z.string().optional(),
    vehicleDescription: z.string().optional(),
    vin: z.string().optional(),
    tagNumber: z.string().optional(),
    mileage: JsonLosslessNumberInputSchema.optional(),
    serviceWriter: z.string().optional(),
    dateOpened: z.string().optional(),
    serviceGroup: z.string().optional(),
    mainPhone: PhoneNumberSchema.optional(),
    billingTotalsByPillar: DealertrackServiceBillingTotalsSchema.optional(),
});

export const DealertrackDealTransactionExtensionSchema = z.object({
    homePhone: PhoneNumberSchema.optional(),
    cellPhone: PhoneNumberSchema.optional(),
    email: z.string().optional(),
    county: z.string().optional(),
    stockNumber: z.string().optional(),
    vehicleDescription: z.string().optional(),
    vin: z.string().optional(),
    odometer: JsonLosslessNumberInputSchema.optional(),
    tradeDescription: z.string().optional(),
    msrp: JsonLosslessNumberInputSchema.optional(),
    price: JsonLosslessNumberInputSchema.optional(),
    downPayment: JsonLosslessNumberInputSchema.optional(),
    tradeAllowance: JsonLosslessNumberInputSchema.optional(),
    payoff: JsonLosslessNumberInputSchema.optional(),
    rebate: JsonLosslessNumberInputSchema.optional(),
    apr: JsonLosslessNumberInputSchema.optional(),
    termMonths: JsonLosslessNumberInputSchema.optional(),
    paymentAmount: JsonLosslessNumberInputSchema.optional(),
    paymentFrequency: z.string().optional(),
    saleDate: z.string().optional(),
    deliverDate: z.string().optional(),
    firstPaymentDate: z.string().optional(),
    amountOptions: JsonLosslessNumberInputSchema.optional(),
    serviceContractAmount: JsonLosslessNumberInputSchema.optional(),
    insuranceAmount: JsonLosslessNumberInputSchema.optional(),
    accessoriesAmount: JsonLosslessNumberInputSchema.optional(),
    lender: z.string().optional(),
    dealTax: JsonLosslessNumberInputSchema.optional(),
    taxGroup: z.string().optional(),
    salespersonPrimary: z.string().optional(),
    salespersonSecondary: z.string().optional(),
    financeManager: z.string().optional(),
    salesManager: z.string().optional(),
}) satisfies z.ZodType<DealertrackDealTransactionExtension>;

export const DealertrackLeasingTransactionExtensionSchema = z.object({
    homePhone: PhoneNumberSchema.optional(),
    cellPhone: PhoneNumberSchema.optional(),
    email: z.string().optional(),
    county: z.string().optional(),
    stockNumber: z.string().optional(),
    dealStatus: z.string().optional(),
    vehicleDescription: z.string().optional(),
    vin: z.string().optional(),
    odometer: JsonLosslessNumberInputSchema.optional(),
    tradeDescription: z.string().optional(),
    msrp: JsonLosslessNumberInputSchema.optional(),
    price: JsonLosslessNumberInputSchema.optional(),
    tradeAllowance: JsonLosslessNumberInputSchema.optional(),
    payoff: JsonLosslessNumberInputSchema.optional(),
    rebate: JsonLosslessNumberInputSchema.optional(),
    residualPercentage: JsonLosslessNumberInputSchema.optional(),
    apr: JsonLosslessNumberInputSchema.optional(),
    moneyFactor: JsonLosslessNumberInputSchema.optional(),
    termMonths: JsonLosslessNumberInputSchema.optional(),
    cashReceived: JsonLosslessNumberInputSchema.optional(),
    upfrontAmount: JsonLosslessNumberInputSchema.optional(),
    cashCapReduction: JsonLosslessNumberInputSchema.optional(),
    saleDate: z.string().optional(),
    deliverDate: z.string().optional(),
    secondPaymentDate: z.string().optional(),
    amountOptions: JsonLosslessNumberInputSchema.optional(),
    insuranceAmount: JsonLosslessNumberInputSchema.optional(),
    capReductionAmount: JsonLosslessNumberInputSchema.optional(),
    accessoriesAmount: JsonLosslessNumberInputSchema.optional(),
    serviceContractAmount: JsonLosslessNumberInputSchema.optional(),
    lessor: z.string().optional(),
    acquisitionFee: JsonLosslessNumberInputSchema.optional(),
    leaseTax: JsonLosslessNumberInputSchema.optional(),
    cashRequired: JsonLosslessNumberInputSchema.optional(),
    taxGroup: z.string().optional(),
    securityDeposit: JsonLosslessNumberInputSchema.optional(),
    feeAmount: JsonLosslessNumberInputSchema.optional(),
    functionCode: z.string().optional(),
    permitNumberExpiration: z.string().optional(),
    annualMiles: JsonLosslessNumberInputSchema.optional(),
    pdi: z.string().optional(),
    profitCode: z.string().optional(),
    salespersonPrimary: z.string().optional(),
    salespersonSecondary: z.string().optional(),
    financeManager: z.string().optional(),
    salesManager: z.string().optional(),
    paymentAmount: JsonLosslessNumberInputSchema.optional(),
    paymentFrequency: z.string().optional(),
}) satisfies z.ZodType<DealertrackLeasingTransactionExtension>;

const DealertrackBaseTransactionDetailsShape = {
    amount: JsonNonnegativeLosslessNumberInputSchema,
    detectedAmount: JsonPositiveLosslessNumberInputSchema.optional(),
    tax: JsonLosslessNumberInputSchema.optional(),
    fee: JsonLosslessNumberInputSchema.optional(),
    surcharge: JsonLosslessNumberInputSchema.optional(),
    currency: z.string().min(1),
    merchant_transaction_id: z.string().optional(),
    customerName: z.string().optional(),
    mainPhone: PhoneNumberSchema.optional(),
    otherPhones: z.array(PhoneNumberSchema).default([]),
    mainEmail: z.string().email().optional(),
    address: AddressSchema.optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
    ipw_customer_id: z.string().optional(),
    ipw_system_id: z.literal(ThirdPartySystemId.DEALERTRACK),
};

export const DealertrackBaseTransactionDetailsSchema: z.ZodObject<
    typeof DealertrackBaseTransactionDetailsShape
> = z.object(DealertrackBaseTransactionDetailsShape);

export const DealertrackDealBaseTransactionDetailsSchema: z.ZodObject<
    Omit<typeof DealertrackBaseTransactionDetailsShape, 'tax'>
> = DealertrackBaseTransactionDetailsSchema.omit({ tax: true });

export const DealertrackPartsTransactionDetailsV1Schema =
    DealertrackBaseTransactionDetailsSchema.extend({
        transaction_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_PARTS),
        transaction_details_type_version: z.literal(1),
        // transaction_details_payload: DealertrackPartsTransactionExtensionSchema,
        dealertrack_screen: z.string().optional(),
    }).merge(DealertrackPartsTransactionExtensionSchema);

export const DealertrackServiceTransactionDetailsV1Schema =
    DealertrackBaseTransactionDetailsSchema.extend({
        transaction_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_SERVICE),
        transaction_details_type_version: z.literal(1),
        // transaction_details_payload: DealertrackServiceTransactionExtensionSchema,
        dealertrack_screen: z.string().optional(),
    }).merge(DealertrackServiceTransactionExtensionSchema);

export const DealertrackDealTransactionDetailsV1Schema =
    DealertrackDealBaseTransactionDetailsSchema.extend({
        transaction_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_DEAL),
        transaction_details_type_version: z.literal(1),
        // transaction_details_payload: DealertrackDealTransactionExtensionSchema,
        dealertrack_screen: z.string().optional(),
    }).merge(DealertrackDealTransactionExtensionSchema);

export const DealertrackLeasingTransactionDetailsV1Schema =
    DealertrackDealBaseTransactionDetailsSchema.extend({
        transaction_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_LEASING),
        transaction_details_type_version: z.literal(1),
        dealertrack_screen: z.string().optional(),
    }).merge(DealertrackLeasingTransactionExtensionSchema);

const DealertrackArStatementCustomersTransactionDetailsV1Shape = {
    transaction_details_type_id: z.literal(
        TransactionDetailsTypeId.DEALERTRACK_AR_STATEMENT_CUSTOMERS
    ),
    transaction_details_type_version: z.literal(1),
    dealertrack_screen: z.string().optional(),
};

export const DealertrackArStatementCustomersTransactionDetailsV1Schema: z.ZodObject<
    typeof DealertrackBaseTransactionDetailsShape &
        typeof DealertrackArStatementCustomersTransactionDetailsV1Shape
> = DealertrackBaseTransactionDetailsSchema.extend(
    DealertrackArStatementCustomersTransactionDetailsV1Shape
) satisfies z.ZodType<DealertrackArStatementCustomersTransactionDetails>;

export const DealertrackTransactionDetailsSchema: z.ZodType<DealertrackTransactionDetails> =
    z.discriminatedUnion('transaction_details_type_id', [
        DealertrackPartsTransactionDetailsV1Schema,
        DealertrackServiceTransactionDetailsV1Schema,
        DealertrackDealTransactionDetailsV1Schema,
        DealertrackLeasingTransactionDetailsV1Schema,
        DealertrackArStatementCustomersTransactionDetailsV1Schema,
    ]);
